import {formatDate} from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {EMPTY, map, switchMap, tap} from "rxjs";
import {AuthorizationServiceProvider} from "../../services/authorization-service.provider";
import {ModalComponent} from "../../../utils/modal/modal.component";
import {CountryOption, CountryService} from "../../../services/country.service";
import {catchError} from "rxjs/operators";
import {
  ContactType,
  Gender,
  V3Address,
  V3ContactEntry,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3Settings
} from "../../model/v3/people";
import {V3FileItem} from "../../model/v3/file";
import {Language} from "../../model/casa/casa-model";
import {HttpEvent, HttpEventType, HttpResponse} from "@angular/common/http";


@Component({
    selector: 'personal-profile-form',
    templateUrl: './personal-profile-form.component.html',
    styleUrls: ['./personal-profile-form.component.scss'],
    standalone: false
})
export class PersonalProfileFormComponent implements OnInit {
  countries: CountryOption[]
  _phone: V3ContactEntry
  _skype: V3ContactEntry
  private _contacts: V3ContactEntry[] = [];
  private _sensitiveDetails: V3PersonalSensitiveDetails
  uploadProgress: number;
  private personId: number;
  @ViewChild('fileInput', {static: false}) fileInput: any;

  @ViewChild('deleteAccountInfoModal', { static: true })
  deleteAccountInfoModal: ModalComponent;
  @Input()
  saved: boolean = false

  @Input() email: string

  @Input()
  role: "student" | "teacher"

  @Input()
  busy: boolean

  get phoneNumber() {
    return this.contacts.find(c => c.entryType === ContactType.PHONE).value;
  }

  set phoneNumber(value: string) {
    this._phone = this.contacts.find(c => c.entryType === ContactType.PHONE);
    this._phone.isDefault = true
    this._phone.name = "Phone"
    this._phone.value = value
  }

  get skypeInfo() {
    return this.contacts.find(c => c.entryType === ContactType.SKYPE).value;
  }

  set skypeInfo(value: string) {
    this._skype = this.contacts.find(c => c.entryType === ContactType.SKYPE);
    this._skype.isDefault = true
    this._skype.name = "skype"
    this._skype.value = value
  }

  @Input() personDetails: V3PersonDetails
  @Input() settings: V3Settings
  @Input() address: V3Address = {} as V3Address
  @Input() photo: V3FileItem

  @Input()
  set contacts(value: V3ContactEntry[]) {
    this._contacts = value || []
    this.decomposeContacts()
  }

  get contacts(): V3ContactEntry[] {
    return this._contacts;
  }

  @Input()
  set sensitiveDetails(value: V3PersonalSensitiveDetails) {
    this._sensitiveDetails = value || {} as V3PersonalSensitiveDetails;
    this.convertBirthDate()
  }

  get sensitiveDetails(): V3PersonalSensitiveDetails {
    return this._sensitiveDetails;
  }

  @Output() savePersonDetails = new EventEmitter<V3PersonDetails>();
  @Output() saveSensitiveDetails = new EventEmitter<V3PersonalSensitiveDetails>()
  @Output() saveSettings = new EventEmitter<V3Settings>()
  @Output() saveAddress = new EventEmitter<V3Address>()
  @Output() saveContacts = new EventEmitter<V3ContactEntry[]>()
  @Output() savePhoto = new EventEmitter<V3FileItem>()
  @Output() save = new EventEmitter<void>() //event to confirm saving

  constructor(
    private provider: NativeServiceApiProvider,
    private authProvider: AuthorizationServiceProvider,
    private countryService: CountryService,
  ) {
    this.authProvider.getAuthDetailsService().pipe(
      switchMap(api => api.getSelfPersonId()),
      tap(personId => this.personId = personId),
      switchMap(_ => this.countryService.getCountries()),
      tap(countries => this.countries = countries),
    ).subscribe()
  }


  ngOnInit(): void {
  }

  private decomposeContacts() {
    let contacts = this.contacts;
    if (contacts == null) {
      contacts = [];
      this.contacts = contacts;
    }
    this._phone = contacts.find( c => c.entryType === ContactType.PHONE);
    if (this._phone == null) {
      this._phone = {} as V3ContactEntry;
      this._phone.entryType = ContactType.PHONE;
      this._phone.name = "Phone";
      this.contacts.push(this._phone);
    }
    this._skype = contacts.find( c => c.entryType === ContactType.SKYPE);
    if (this._skype == null) {
      this._skype = {} as V3ContactEntry;
      this._skype.entryType = ContactType.SKYPE;
      this._skype.name = "Skype";
      this.contacts.push(this._skype);
    }
  }

  private convertBirthDate() {
    if (this.sensitiveDetails && this.sensitiveDetails.birthDate) { // && isNumeric(this.profile.birthDate)
      this.sensitiveDetails.birthDate = formatDate(this.sensitiveDetails.birthDate, "yyyy-MM-dd", this.settings.locale) as unknown as number;
    }
  }

  isBusy() {
    return this.busy
  }

  isDisabled() {
    return this.busy
  }

  isStudentForm() {
    return this.role === 'student'
  }

  isReady() {
    return this.personDetails != null &&
      this.sensitiveDetails != null &&
      this.settings != null &&
      this.address != null &&
      this.contacts != null
  }

  getProfilePhoto() {
    return this.photo ? 'url(' + this.photo?.publicAccessUrl + ')' : null
  }

  getCountries() {
    return this.countries
  }

  getLanguages() {
    return Object.keys(Language)
  }

  getGenders() {
    return [Gender.Female, Gender.Male, Gender.Other]
  }

  getGenderTranslationCode(gender: Gender) {
    return `PROFILE.FORM.PERSONAL_INFO.${gender.toUpperCase()}`
  }

  getLanguageByCode(code: string) {
    return Language[code]
  }

  getButtonContent() {
    return this.saved? 'BUTTON.PROFILE_SAVED' : 'BUTTON.SAVE'
  }

  onImageUpload(target: EventTarget) {
    const input = target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;
    this.provider.casa().pipe(
      switchMap( api => {
        if (!api.supportsFileSending()) return EMPTY;
        this.uploadProgress = 0;
        return api.uploadFile(input.files[0])
      }),
      catchError(() => this.uploadProgress = null)
    ).subscribe((event: HttpEvent<any>) => {
      if (event.type === HttpEventType.UploadProgress) {
        this.uploadProgress = Math.round(100 * event.loaded / event.total);
      } else if (event instanceof HttpResponse) {
        this.photo = event.body as V3FileItem;
          this.uploadProgress = null;
        }
    });
  }

  isImageUploadSupported() {
    return this.provider.casa().pipe(
      map( api => api.supportsFileSending())
    )
  }

  deleteAccount() {
    this.provider.casa().pipe(
      switchMap(api => api.deletePerson(this.personId)),
    ).subscribe(_ => this.deleteAccountInfoModal.show())
  }

  logout() {
    this.authProvider.getCommonRoutines()
      .subscribe(service => service.logout())
  }

  onPersonalDetailsSave() {
    this.savePersonDetails.emit(this.personDetails);
    this.saveSensitiveDetails.emit(this.sensitiveDetails);
    this.saveAddress.emit(this.address)
    this.saveSettings.emit(this.settings)
    this.save.emit()
  }

  onPhotoSave() {
    if (!this.photo) return
    this.savePhoto.emit(this.photo);
    this.save.emit()
  }

  onContactSave() {
    this.saveContacts.emit(this.contacts);
    this.saveAddress.emit(this.address)
    this.save.emit()
  }

  onOtherSave() {
    this.saveSensitiveDetails.emit(this.sensitiveDetails);
    this.save.emit()
  }

  onWholeFormSave() {
    this.savePersonDetails.emit(this.personDetails);
    this.saveAddress.emit(this.address)
    this.saveContacts.emit(this.contacts);
    this.saveSettings.emit(this.settings)
    if (this.photo)
      this.savePhoto.emit(this.photo);
    this.saveSensitiveDetails.emit(this.sensitiveDetails);
    this.save.emit()
  }
}
